/* eslint-disable consistent-return, default-case, no-param-reassign */
/*
 *
 * Payment Sources reducer
 *
 */

// immer's 'original' method used to reference values currently in state
import produce, { original } from 'immer';
import isEmpty from 'lodash/isEmpty';

import { RESET_SWITCH_LOAN_STATE, RESET_USER_STATE } from '../../Main/constants';
import {
  CLEAR_NEW_INFO,
  NACHA_VALIDATION_FAILURE,
  RESET_BANNER_ERRORS,
  RESET_STATE,
  SET_EXISTING_INFO,
  SET_INPUT,
  SET_INPUT_ERROR,
  SET_IS_SOURCE_DRAWER_OPEN,
  SET_USE_EXISTING,
  SET_VALIDATE_ROUTING_ERROR,
  UPDATE_FORM_ERRORS,
  VALIDATE_ROUTING,
  VALIDATE_ROUTING_ERROR_MESSAGE,
  VALIDATE_ROUTING_FAILURE,
  VALIDATE_ROUTING_SUCCESS,
} from './constants';

export const initialState = {
  accountInfoExisting: {
    autopay: {},
    otp: {},
  },
  accountInfoNew: {},
  bannerError: false,
  formErrors: {
    accountNumber: '',
    accountNumberConfirm: '',
    nameOnAccount: '',
    routingNumber: '',
  },
  formValues: {
    accountNumber: '',
    accountNumberConfirm: '',
    accountType: 'Checking',
    nameOnAccount: '',
    routingNumber: '',
  },
  isSourceDrawerOpen: false,
  useAccountInfoExisting: {
    autopay: false,
    otp: false,
  },
  useAccountInfoNew: false,
  validateRoutingError: false,
  validateRoutingLoading: false,
  validateRoutingSuccess: false,
};

const paymentSourcesReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case CLEAR_NEW_INFO: {
      draft.bannerError = initialState.bannerError;
      draft.accountInfoNew = {};
      draft.useAccountInfoNew = false;
      draft.validateRoutingSuccess = false;
      break;
    }
    case NACHA_VALIDATION_FAILURE: {
      const { bannerError } = payload;
      draft.bannerError = bannerError;
      draft.validateRoutingLoading = false;
      break;
    }
    case RESET_BANNER_ERRORS: {
      draft.bannerError = initialState.bannerError;
      draft.validateRoutingError = initialState.validateRoutingError;
      break;
    }
    case RESET_STATE: {
      return {
        ...initialState,
        accountInfoExisting: draft.accountInfoExisting,
        useAccountInfoExisting: {
          autopay: !isEmpty(draft.accountInfoExisting.autopay),
          otp: !isEmpty(draft.accountInfoExisting.otp),
        },
      };
    }
    case RESET_SWITCH_LOAN_STATE: {
      return initialState;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case SET_EXISTING_INFO: {
      const { existingAccountInformation, paymentType } = payload;
      draft.accountInfoExisting[paymentType] = existingAccountInformation;
      if (!original(draft).useAccountInfoNew) {
        draft.useAccountInfoExisting[paymentType] = true;
      }
      break;
    }
    case SET_INPUT: {
      const { field, value } = payload;
      draft.formValues[field] = value;
      if (original(draft).formErrors[field]) {
        if (field === 'accountNumber') {
          draft.formErrors.accountNumberConfirm = '';
        }
        draft.formErrors[field] = '';
      }
      break;
    }
    case SET_INPUT_ERROR: {
      const { field, value } = payload;
      draft.formErrors[field] = value;
      break;
    }
    case SET_IS_SOURCE_DRAWER_OPEN: {
      const { value } = payload;
      draft.isSourceDrawerOpen = value;
      break;
    }
    case SET_USE_EXISTING: {
      const { paymentType, value } = payload;
      draft.useAccountInfoExisting[paymentType] = value;
      draft.bannerError = initialState.bannerError;
      if (!isEmpty(original(draft).accountInfoNew)) {
        draft.useAccountInfoNew = !value;
      }
      break;
    }
    case SET_VALIDATE_ROUTING_ERROR: {
      const { value } = payload;
      draft.validateRoutingError = value;
      break;
    }
    case UPDATE_FORM_ERRORS: {
      const { account, bannerError } = payload;
      draft.bannerError = bannerError;
      Object.keys(account).forEach((key) => {
        draft.formErrors[key] = account[key];
      });
      const hasErrors = Object.values(draft.formErrors).reduce((acc, value) => acc || !!value);
      if (hasErrors || bannerError) {
        draft.isSourceDrawerOpen = true;
      }
      break;
    }
    case VALIDATE_ROUTING: {
      draft.bannerError = initialState.bannerError;
      draft.validateRoutingLoading = true;
      break;
    }
    case VALIDATE_ROUTING_FAILURE: {
      draft.validateRoutingError = VALIDATE_ROUTING_ERROR_MESSAGE;
      draft.validateRoutingLoading = false;
      draft.formValues = initialState.formValues;
      break;
    }
    case VALIDATE_ROUTING_SUCCESS: {
      draft.validateRoutingLoading = false;
      const { isValid } = payload;
      if (isValid) {
        const {
          accountNumber,
          accountType,
          nameOnAccount,
          routingNumber,
        } = original(draft).formValues;
        draft.accountInfoNew = {
          accountNumber,
          accountType,
          nameOnAccount,
          routingNumber,
        };
        draft.useAccountInfoNew = true;
        draft.formValues = initialState.formValues;
        draft.validateRoutingSuccess = true;
        draft.validateRoutingError = initialState.validateRoutingError;
      }
      break;
    }
  }
}, initialState);

export default paymentSourcesReducer;
